import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@/store/index";
import {
  openTab as openReportTab,
  closeTab as closeReportTab,
  initPortalTabs,
} from "@/store/slices/reportTabsSlice";
import type { ReportTab } from "@/store/slices/reportTabsSlice";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { SkyButton, SkyLoader, useSkyTheme } from "@skypointcloud/skypoint-ui";
import {
  Settings,
  Star,
  Plus,
  X,
  BarChart3,
  ChevronLeft,
  ShieldCheck,
  Users,
  ListChecks,
  Timer,
  TrendingUp,
  FileText,
} from "lucide-react";
import { ROUTES } from "@/components/shared/constant";
import { useGetPortalQuery, useGetCustomReportsQuery } from "@/store/api/portalsApi";
import { useMsalAuth } from "@/hooks/useMsalAuth";
import { useAuthenticatedImage } from "@/hooks/useAuthenticatedImage";
import {
  PORTAL_TAB_DEFINITIONS,
  DEFAULT_TAB,
  ALL_TAB_IDS,
} from "@/views/portals/shared/portalTabDefinitions";
import { usePortalFilters } from "@/hooks/usePortalFilters";

// Match portal routes that should show the top bar
const PORTAL_ROUTE_PATTERN = /^\/portals\/([^/]+)\/(list|board|calendar|queues|settings|reports)/;

// ─── REPORT TABS DEFINITION ─────────────────────────────────────────────────

interface ReportTabDef {
  key: string;
  label: string;
  icon: React.ElementType;
  matchPath: string; // substring to match in location.pathname
  getRoute: (portalKey: string) => string;
}

const REPORT_TABS: ReportTabDef[] = [
  {
    key: "overview",
    label: "Overview",
    icon: BarChart3,
    matchPath: "/reports",
    getRoute: (pk) => ROUTES.PORTAL_REPORTS.replace(":portalKey", pk),
  },
  {
    key: "created-vs-resolved",
    label: "Created vs Resolved",
    icon: BarChart3,
    matchPath: "/reports/created-vs-resolved",
    getRoute: (pk) => ROUTES.PORTAL_REPORT_CREATED_VS_RESOLVED.replace(":portalKey", pk),
  },
  {
    key: "sla-compliance",
    label: "SLA Compliance",
    icon: ShieldCheck,
    matchPath: "/reports/sla-compliance",
    getRoute: (pk) => ROUTES.PORTAL_REPORT_SLA_COMPLIANCE.replace(":portalKey", pk),
  },
  {
    key: "agent-performance",
    label: "Agent Performance",
    icon: Users,
    matchPath: "/reports/agent-performance",
    getRoute: (pk) => ROUTES.PORTAL_REPORT_AGENT_PERFORMANCE.replace(":portalKey", pk),
  },
  {
    key: "request-types",
    label: "Request Types",
    icon: ListChecks,
    matchPath: "/reports/request-types",
    getRoute: (pk) => ROUTES.PORTAL_REPORT_REQUEST_TYPES.replace(":portalKey", pk),
  },
  {
    key: "resolution-time",
    label: "Resolution Time",
    icon: Timer,
    matchPath: "/reports/resolution-time",
    getRoute: (pk) => ROUTES.PORTAL_REPORT_RESOLUTION_TIME.replace(":portalKey", pk),
  },
  {
    key: "customer-satisfaction",
    label: "Customer Satisfaction",
    icon: Star,
    matchPath: "/reports/csat",
    getRoute: (pk) => ROUTES.PORTAL_REPORT_CSAT.replace(":portalKey", pk),
  },
  {
    key: "nps",
    label: "Net Promoter Score",
    icon: TrendingUp,
    matchPath: "/reports/nps",
    getRoute: (pk) => ROUTES.PORTAL_REPORT_NPS.replace(":portalKey", pk),
  },
];

function getActiveReportTab(pathname: string): string {
  // Match the most specific path first (longer matchPath = more specific)
  const sorted = [...REPORT_TABS].sort((a, b) => b.matchPath.length - a.matchPath.length);
  for (const tab of sorted) {
    if (pathname.includes(tab.matchPath)) return tab.key;
  }
  return "overview";
}

// ─── PORTAL TOP BAR ──────────────────────────────────────────────────────────

export function PortalTopBar() {
  const location = useLocation();
  const params = useParams<{ portalKey: string }>();

  const portalKeyFromParams = params.portalKey || "";

  const pathMatch = location.pathname.match(PORTAL_ROUTE_PATTERN);
  const portalKey = portalKeyFromParams || (pathMatch ? pathMatch[1] : "");

  if (!pathMatch && !portalKey) return null;
  if (location.pathname === "/portals" || location.pathname === "/portals/dashboard") return null;
  if (location.pathname.includes("/settings")) return null;
  if (/^\/portals\/[^/]+\/items\/[^/]+/.test(location.pathname)) return null;

  return <PortalTopBarInner portalKey={portalKey} />;
}

function PortalTopBarInner({ portalKey }: { portalKey: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useSkyTheme();

  const { getQueryString } = useMsalAuth();

  const { data: portal, isLoading } = useGetPortalQuery(portalKey, { skip: !portalKey });
  const logoSrc = useAuthenticatedImage(
    portal?.logoUrl ? `/api/v1/portals/${portalKey}/logo${getQueryString()}` : null,
  );

  const { workType: activeWorkType } = usePortalFilters();
  const hasWorkType = !!activeWorkType;

  const isOnReports = /\/reports/.test(location.pathname);

  // Fetch custom reports for dynamic tabs
  const { data: customReportsData } = useGetCustomReportsQuery(
    { portalKey },
    { skip: !portalKey || !isOnReports },
  );
  const customReports = customReportsData?.data ?? [];

  // ── Redux report tabs ──
  const dispatch = useDispatch();
  const reportTabs = useSelector(
    (state: RootState) => state.reportTabsReducer?.tabsByPortal?.[portalKey] ?? [],
  );
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const plusDropdownRef = useRef<HTMLDivElement>(null);
  const suppressAutoOpenRef = useRef(false);

  // Init portal tabs on mount
  useEffect(() => {
    if (isOnReports) dispatch(initPortalTabs({ portalKey }));
  }, [portalKey, isOnReports, dispatch]);

  // Auto-open tab when navigating directly to a report URL
  useEffect(() => {
    if (!isOnReports) return;
    if (suppressAutoOpenRef.current) {
      suppressAutoOpenRef.current = false;
      return;
    }
    const path = location.pathname;

    // Check if on a pre-built report
    for (const tab of REPORT_TABS) {
      if (tab.key === "overview") continue;
      if (path.includes(tab.matchPath) && !reportTabs.some((t) => t.id === tab.key)) {
        dispatch(
          openReportTab({
            portalKey,
            tab: {
              id: tab.key,
              type: "prebuilt",
              label: tab.label,
              route: tab.getRoute(portalKey),
            },
          }),
        );
      }
    }

    // Check if on a custom report
    const customMatch = path.match(/\/reports\/custom\/([^/?]+)/);
    if (customMatch) {
      const reportId = customMatch[1];
      if (!reportTabs.some((t) => t.id === reportId)) {
        const report = customReports.find((r) => r.id === reportId);
        dispatch(
          openReportTab({
            portalKey,
            tab: {
              id: reportId,
              type: "custom",
              label: report?.name ?? "Report",
              route: ROUTES.PORTAL_CUSTOM_REPORT.replace(":portalKey", portalKey).replace(
                ":reportId",
                reportId,
              ),
            },
          }),
        );
      }
    }
  }, [location.pathname, isOnReports, portalKey, customReports, reportTabs, dispatch]);

  // Close plus menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideButton = plusMenuRef.current?.contains(target);
      const isInsideDropdown = plusDropdownRef.current?.contains(target);
      if (!isInsideButton && !isInsideDropdown) {
        setShowPlusMenu(false);
      }
    };
    if (showPlusMenu) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showPlusMenu]);

  // Compute active tab from URL
  const activeTabId = (() => {
    const path = location.pathname;
    const customMatch = path.match(/\/reports\/custom\/([^/?]+)/);
    if (customMatch) return customMatch[1];
    const sorted = [...REPORT_TABS].sort((a, b) => b.matchPath.length - a.matchPath.length);
    for (const tab of sorted) {
      if (path.includes(tab.matchPath)) return tab.key;
    }
    return "overview";
  })();

  // Get route for a tab
  const getTabRoute = (tab: ReportTab): string => {
    if (tab.id === "overview") return ROUTES.PORTAL_REPORTS.replace(":portalKey", portalKey);
    if (tab.type === "prebuilt") {
      const def = REPORT_TABS.find((t) => t.key === tab.id);
      return def ? def.getRoute(portalKey) : ROUTES.PORTAL_REPORTS.replace(":portalKey", portalKey);
    }
    return (
      tab.route ||
      ROUTES.PORTAL_CUSTOM_REPORT.replace(":portalKey", portalKey).replace(":reportId", tab.id)
    );
  };

  // Handle close tab
  const handleCloseTab = (tabId: string) => {
    suppressAutoOpenRef.current = true;
    dispatch(closeReportTab({ portalKey, tabId }));
    // If closing the active tab, navigate to nearest
    if (tabId === activeTabId) {
      const idx = reportTabs.findIndex((t) => t.id === tabId);
      const next = reportTabs[idx - 1] ?? reportTabs[0];
      if (next) navigate(getTabRoute(next));
    }
  };

  // Reports available to add via + menu (not already open)
  const openTabIds = new Set(reportTabs.map((t) => t.id));
  const availablePrebuilt = REPORT_TABS.filter(
    (t) => t.key !== "overview" && !openTabIds.has(t.key),
  );
  const availableCustom = customReports.filter((r) => !openTabIds.has(r.id));

  // Redirect off Board (work-type-specific because of workflows) when work type is cleared.
  // Calendar and Queues are portal-level and stay accessible without a work type.
  useEffect(() => {
    if (hasWorkType) return;
    if (isOnReports) return;
    const path = location.pathname;
    if (path.includes("/board") && portalKey) {
      navigate(
        PORTAL_TAB_DEFINITIONS[DEFAULT_TAB].route.replace(":portalKey", portalKey) +
          location.search,
        { replace: true },
      );
    }
  }, [hasWorkType, isOnReports, location.pathname, portalKey, navigate, location.search]);

  const currentView = (() => {
    const path = location.pathname;
    if (path.includes("/board")) return "board";

    if (path.includes("/calendar")) return "calendar";
    if (path.includes("/queues")) return "queues";
    if (path.includes("/list")) return "list";
    return "list";
  })();

  const color = portal?.color || "#0078D4";

  if (isLoading) {
    return (
      <div className="sticky top-[calc(52px+var(--new-version-h))] h-12 hidden md:flex items-center bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 z-[900] px-4">
        <SkyLoader size="tiny" />
      </div>
    );
  }

  // Preserve workType (and other filters) when switching tabs
  const searchParams = location.search;

  const _activeReportTab = isOnReports ? getActiveReportTab(location.pathname) : null;

  // ─── REPORTS LAYOUT ──────────────────────────────────────────────────────────
  if (isOnReports) {
    return (
      <div className="sticky top-[calc(52px+var(--new-version-h))] hidden md:flex flex-col bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 z-[900]">
        {/* Row 1: Portal info + breadcrumb + actions */}
        <div className="flex items-center h-12 pl-4 pr-3 gap-0">
          {/* Portal info */}
          <div className="flex items-center gap-2.5 shrink-0">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={portal?.name || portalKey}
                className="w-10 h-10 rounded-full object-cover shrink-0"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold shrink-0"
                style={{
                  color: isDarkMode ? "#fff" : color,
                  backgroundColor: isDarkMode ? color : `${color}22`,
                }}
              >
                {(portal?.name || portalKey).charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-[15px] font-bold text-gray-900 dark:text-white whitespace-nowrap">
              {portal?.name || portalKey}
            </span>
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.5px] px-1.5 rounded text-white leading-4"
              style={{ backgroundColor: color }}
            >
              {portalKey}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
              / <span className="text-blue-500 font-medium">Reports</span>
            </span>
          </div>

          <div className="flex-1" />

          {/* Actions — Reports page only shows navigation back, not ticket-Create */}
          <div className="flex gap-1.5 items-center shrink-0">
            <SkyButton
              appearance="outline"
              icon={ChevronLeft}
              size="small"
              onClick={() =>
                navigate(
                  PORTAL_TAB_DEFINITIONS[DEFAULT_TAB].route.replace(":portalKey", portalKey) +
                    searchParams,
                )
              }
            >
              Back to List
            </SkyButton>
          </div>
        </div>

        {/* Row 2: Browser-style report tabs */}
        <div className="px-2 flex items-end gap-0 overflow-x-auto">
          {reportTabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(getTabRoute(tab))}
                className={`group relative flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium border-b-2 transition-all shrink-0 max-w-[180px] ${
                  isActive
                    ? "border-indigo-500 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-900"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <span className="truncate">{tab.label}</span>
                {tab.type !== "pinned" && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseTab(tab.id);
                    }}
                    className={`shrink-0 rounded p-0.5 transition-colors ${
                      isActive
                        ? "text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 dark:hover:bg-indigo-900"
                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100"
                    }`}
                  >
                    <X size={12} />
                  </span>
                )}
              </button>
            );
          })}
          {/* + button */}
          <div
            className="relative shrink-0"
            ref={plusMenuRef}
          >
            <button
              onClick={() => setShowPlusMenu(!showPlusMenu)}
              className="flex items-center justify-center w-7 h-7 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ml-1"
            >
              <Plus size={14} />
            </button>

            {showPlusMenu &&
              createPortal(
                <div
                  ref={(el) => {
                    plusDropdownRef.current = el;
                    if (el && plusMenuRef.current) {
                      const rect = plusMenuRef.current.getBoundingClientRect();
                      el.style.position = "fixed";
                      el.style.top = `${rect.bottom + 4}px`;
                      el.style.left = `${rect.left}px`;
                    }
                  }}
                  className="w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[1000] py-1 max-h-[400px] overflow-y-auto"
                >
                  {availablePrebuilt.length > 0 && (
                    <>
                      <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                        Built-in Reports
                      </p>
                      {availablePrebuilt.map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => {
                            dispatch(
                              openReportTab({
                                portalKey,
                                tab: {
                                  id: tab.key,
                                  type: "prebuilt",
                                  label: tab.label,
                                  route: tab.getRoute(portalKey),
                                },
                              }),
                            );
                            navigate(tab.getRoute(portalKey));
                            setShowPlusMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                        >
                          <tab.icon
                            size={14}
                            className="text-gray-400 shrink-0"
                          />
                          {tab.label}
                        </button>
                      ))}
                    </>
                  )}

                  {availableCustom.length > 0 && (
                    <>
                      <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-1">
                        Custom Reports
                      </p>
                      {availableCustom.map((report) => (
                        <button
                          key={report.id}
                          onClick={() => {
                            const route = ROUTES.PORTAL_CUSTOM_REPORT.replace(
                              ":portalKey",
                              portalKey,
                            ).replace(":reportId", report.id);
                            dispatch(
                              openReportTab({
                                portalKey,
                                tab: { id: report.id, type: "custom", label: report.name, route },
                              }),
                            );
                            navigate(route);
                            setShowPlusMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                        >
                          <FileText
                            size={14}
                            className="text-indigo-400 shrink-0"
                          />
                          {report.name}
                        </button>
                      ))}
                    </>
                  )}

                  <div className="border-t border-gray-100 dark:border-gray-800 mt-1 pt-1">
                    <button
                      onClick={() => {
                        navigate(ROUTES.PORTAL_REPORT_BUILDER.replace(":portalKey", portalKey));
                        setShowPlusMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 flex items-center gap-2 font-medium"
                    >
                      <Plus
                        size={14}
                        className="shrink-0"
                      />
                      Create New Report
                    </button>
                  </div>
                </div>,
                document.body,
              )}
          </div>
        </div>
      </div>
    );
  }

  // ─── DEFAULT (NON-REPORTS) LAYOUT ────────────────────────────────────────────
  return (
    <div className="sticky top-[calc(52px+var(--new-version-h))] h-12 hidden md:flex items-center bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 z-[900] pl-4 pr-3 gap-0">
      {/* Portal info */}
      <div className="flex items-center gap-2.5 shrink-0">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={portal?.name || portalKey}
            className="w-10 h-10 rounded-full object-cover shrink-0"
          />
        ) : (
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold shrink-0"
            style={{
              color: isDarkMode ? "#fff" : color,
              backgroundColor: isDarkMode ? color : `${color}22`,
            }}
          >
            {(portal?.name || portalKey).charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-[15px] font-bold text-gray-900 dark:text-white whitespace-nowrap">
          {portal?.name || portalKey}
        </span>
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.5px] px-1.5 rounded text-white leading-4"
          style={{ backgroundColor: color }}
        >
          {portalKey}
        </span>
      </div>

      <div className="w-px h-6 bg-gray-100 dark:bg-gray-800 mx-3 shrink-0" />

      {/* View tabs — always visible, non-closable. Portal-level tabs first, then a
          contextual Board tab that only appears when a work type is active. */}
      <div className="flex items-center gap-0 flex-1 min-w-0 overflow-hidden">
        {ALL_TAB_IDS.filter((t) => t !== "board").map((tabId) => {
          const def = PORTAL_TAB_DEFINITIONS[tabId];
          const Icon = def.icon;
          const isActive = currentView === tabId;
          return (
            <div
              key={tabId}
              className={`flex items-center gap-1.5 px-3 h-12 cursor-pointer text-[13px] font-medium whitespace-nowrap border-b-2 select-none shrink-0 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 ${
                isActive
                  ? "text-gray-900 dark:text-white font-semibold border-b-amber-400"
                  : "text-gray-600 dark:text-gray-300 border-b-transparent"
              }`}
              onClick={() => navigate(def.route.replace(":portalKey", portalKey) + searchParams)}
            >
              <Icon />
              {def.label}
            </div>
          );
        })}

        {/* Board — work-type-specific. Appears after a separator with the work type
            name as context so users understand the association. */}
        {hasWorkType &&
          (() => {
            const def = PORTAL_TAB_DEFINITIONS.board;
            const Icon = def.icon;
            const isActive = currentView === "board";
            const wtLabel = activeWorkType
              ? activeWorkType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
              : "";
            return (
              <>
                <div className="mx-2 h-5 w-px bg-gray-200 dark:bg-gray-700 shrink-0" />
                <div
                  className={`flex items-center gap-1.5 px-3 h-12 cursor-pointer text-[13px] font-medium whitespace-nowrap border-b-2 select-none shrink-0 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    isActive
                      ? "text-gray-900 dark:text-white font-semibold border-b-amber-400"
                      : "text-gray-600 dark:text-gray-300 border-b-transparent"
                  }`}
                  onClick={() =>
                    navigate(def.route.replace(":portalKey", portalKey) + searchParams)
                  }
                  title={`Board is specific to the ${wtLabel} work type`}
                >
                  <Icon />
                  {def.label}
                  <span className="text-[11px] font-normal text-gray-400 dark:text-gray-500">
                    · {wtLabel}
                  </span>
                </div>
              </>
            );
          })()}
      </div>

      {/* Actions */}
      <div className="flex gap-1 items-center shrink-0 ml-auto">
        {currentView !== "queues" && (
          <SkyButton
            appearance="primary"
            icon={Plus}
            size="small"
            onClick={() => navigate(ROUTES.PORTAL_ITEM_NEW.replace(":portalKey", portalKey))}
          >
            Create
          </SkyButton>
        )}
        <SkyButton
          appearance="outline"
          icon={BarChart3}
          size="small"
          onClick={() => navigate(ROUTES.PORTAL_REPORTS.replace(":portalKey", portalKey))}
        >
          Reports
        </SkyButton>
        <SkyButton
          appearance="transparent"
          icon={Settings}
          size="small"
          onClick={() => navigate(ROUTES.PORTAL_SETTINGS.replace(":portalKey", portalKey))}
        />
      </div>
    </div>
  );
}
